import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import cmsService from '../../services/cmsService';

export const fetchPages = createAsyncThunk('cms/fetchPages', async (params, { rejectWithValue }) => {
  try {
    const response = await cmsService.getAllPages(params);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch pages');
  }
});

export const fetchPageById = createAsyncThunk('cms/fetchPageById', async (id, { rejectWithValue }) => {
  try {
    const response = await cmsService.getPageById(id);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to fetch page');
  }
});

export const createPage = createAsyncThunk('cms/createPage', async (data, { rejectWithValue }) => {
  try {
    const response = await cmsService.createPage(data);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to create page');
  }
});

export const updatePage = createAsyncThunk('cms/updatePage', async ({ id, data }, { rejectWithValue }) => {
  try {
    const response = await cmsService.updatePage(id, data);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to update page');
  }
});

export const deletePage = createAsyncThunk('cms/deletePage', async (id, { rejectWithValue }) => {
  try {
    await cmsService.deletePage(id);
    return id;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to delete page');
  }
});

export const togglePageStatus = createAsyncThunk('cms/togglePageStatus', async (id, { rejectWithValue }) => {
  try {
    const response = await cmsService.toggleStatus(id);
    return response.data;
  } catch (error) {
    return rejectWithValue(error.response?.data?.message || 'Failed to toggle status');
  }
});

const cmsSlice = createSlice({
  name: 'cms',
  initialState: {
    pages: [],
    currentPage: null,
    totalPages: 0,
    totalItems: 0,
    isLoading: false,
    success: false,
    error: null,
  },
  reducers: {
    resetSuccess: (state) => {
      state.success = false;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentPage: (state) => {
      state.currentPage = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPages.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchPages.fulfilled, (state, action) => {
        state.isLoading = false;
        state.pages = action.payload.data;
        state.totalItems = action.payload.pagination?.totalItems || 0;
        state.totalPages = action.payload.pagination?.totalPages || 0;
      })
      .addCase(fetchPages.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(fetchPageById.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchPageById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentPage = action.payload.data;
      })
      .addCase(fetchPageById.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(createPage.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createPage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.pages.unshift(action.payload.data);
        state.success = true;
      })
      .addCase(createPage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(updatePage.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updatePage.fulfilled, (state, action) => {
        state.isLoading = false;
        const index = state.pages.findIndex((p) => p._id === action.payload.data._id);
        if (index !== -1) {
          state.pages[index] = action.payload.data;
        }
        state.success = true;
      })
      .addCase(updatePage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(deletePage.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deletePage.fulfilled, (state, action) => {
        state.isLoading = false;
        state.pages = state.pages.filter((p) => p._id !== action.payload);
        state.success = true;
      })
      .addCase(deletePage.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      .addCase(togglePageStatus.fulfilled, (state, action) => {
        const index = state.pages.findIndex((p) => p._id === action.payload.data._id);
        if (index !== -1) {
          state.pages[index] = action.payload.data;
        }
      });
  },
});

export const { resetSuccess, clearError, clearCurrentPage } = cmsSlice.actions;
export default cmsSlice.reducer;
